import { PauseCircle, PlayCircle, Cancel } from '@mui/icons-material'
import { Button, Tooltip } from '@mui/material'
import { apiPut } from '../../../generic/Api_Functions'
import { useState, useEffect } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import PausePartSelectEndJobDialog from './PausePartSelectEndJobDialog'
import CancelPartDialog from './CancelPartDialog'
import InputIcon from '@mui/icons-material/Input'
import NextJobDialog from '../nextJob/NextJobDialog'
import { getCellGeneralStatus } from '../../../generic/CellGeneralStatus'
import { useAuthorization } from '../../../generic/hooks/useAuthorization'
import { getPartPendingMoves, EMPTY_PENDING_MOVES } from './partPendingMoves'
const PausePart = (props) => {
  const { t_ } = useSmartTranslation()
  const [showSelectEndJob, setShowSelectEndJob] = useState(false)
  const [checked, setChecked] = useState('')
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [pendingMoves, setPendingMoves] = useState(EMPTY_PENDING_MOVES)
  const [showNextJobDialog, setShowNextJobDialog] = useState(false)
  const partPaused = props.part.status === 'WORKFLOW_PAUSED'
  const isUserAuthorized = useAuthorization()
  // 'touch' renders the large square buttons used by the Monitoring resource-actions
  // dialog (#2745); default 'text' keeps the traceability-header look untouched.
  const touch = props.buttonVariant === 'touch'
  useEffect(() => {
    const fetchStatus = () => {
      const currentStatus = getCellGeneralStatus()
      setChecked(prev => prev !== currentStatus ? currentStatus : prev)
    }
    fetchStatus()
    const interval = setInterval(fetchStatus, 1000)
    return () => clearInterval(interval)
  }, [])

  const onPauseError = () => {
    props.showAlert({
      title: t_('Error pausing part'),
      severity: 'error'
    })
  }

  const onPauseSuccess = () => {
    props.showAlert({
      title: t_('Part has been paused'),
      severity: 'success'
    })
  }

  const onCancelSuccess = () => {
    props.showAlert({
      title: t_('Part has been canceled'),
      severity: 'success'
    })
  }

  const onCancelError = () => {
    props.showAlert({
      title: t_('Error cancelling part'),
      severity: 'error'
    })
  }

  const onPauseWorkflow = async () => {
    try {
      await apiPut('part/pause/' + props.part.id)
      onPauseSuccess()
    } catch (error) {
      if (error.status === 409) {
        // Part is in a job with multiple tasks. Decide if we want to end all tasks
        setShowSelectEndJob(true)
      } else {
        onPauseError()
      }
    }
  }

  const onPauseWorkflowForce = async (endJob) => {
    setShowSelectEndJob(false)
    try {
      await apiPut('part/pause/' + props.part.id + '/' + endJob)
      onPauseSuccess()
    } catch (error) {
      onPauseError()
    }
  }

  const onCancelPart = async (comment) => {
    setShowSelectEndJob(false)
    try {
      // The reason is free text going into a URL path segment: encode it so characters
      // like '/', '#', '%' or newlines (multiline field) cannot break or truncate the route.
      await apiPut('part/cancel/' + props.part.id + '/' + encodeURIComponent(comment))
      onCancelSuccess()
    } catch (error) {
      onCancelError()
    }
  }
  const onResumeWorkflow = async () => {
    try {
      await apiPut('part/resume/' + props.part.id)
      props.showAlert({
        title: t_('Part has been resumed'),
        severity: 'success'
      })
    } catch (error) {
      props.showAlert({
        title: t_('Part could not be resumed'),
        severity: 'error'
      })
    }
  }

  const handleOpenCancelDialog = async () => {
    const moves = await getPartPendingMoves(props.part.id)
    setPendingMoves(moves)
    setShowCancelDialog(true)
  }

  const onConfirmCancelDialog = (comment) => {
    setShowCancelDialog(false)
    onCancelPart(comment)
  }

  return (
    <>
      {/* NextJobDialog wraps its dialog in an always-rendered <div>; when the Next job
          button is hidden (e.g. the Monitoring touch grid) that stray node must not exist,
          or it takes an empty cell in the parent grid. It is unreachable anyway without the
          button, so skip it entirely. */}
      {!props.hideNextJob && (
        <NextJobDialog
          setOpen={showNextJobDialog}
          cancelCallback={() => setShowNextJobDialog(false)}
          partId={props.part.id}
          partCode={props.part.code}
        />
      )}

      <PausePartSelectEndJobDialog
        setOpen={showSelectEndJob}
        cancelCallback={() => setShowSelectEndJob(false)}
        part={props.part}
        pauseNowCallback={() => { onPauseWorkflowForce(false) }}
        pauseAtJobEndCallback={() => { onPauseWorkflowForce(true) }}
      />
      <CancelPartDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={onConfirmCancelDialog}
        pendingMoves={pendingMoves}
      />
      {props.part && (touch
        ? (
          <>
            {/* !props.part.status → the part is still loading (the monitoring dialog seeds
                { id } first): keep the actions disabled until the real status/finished
                fields arrive, so e.g. a RAW part's Cancel never flashes enabled. */}
            <button
              type='button'
              className='mcx-touch-btn mcx-touch-btn--lg'
              data-testid='resource-action-pause'
              onClick={partPaused ? onResumeWorkflow : onPauseWorkflow}
              disabled={!props.part.status || props.part.finished || !isUserAuthorized('PAUSE_WORKFLOW')}
            >
              {partPaused ? <PlayCircle sx={{ fontSize: 32 }} /> : <PauseCircle sx={{ fontSize: 32 }} />}
              <span className='mcx-touch-btn__label'>{partPaused ? t_('Resume workflow') : t_('Pause workflow')}</span>
            </button>
            {/* Tooltip + span wrapper (same pattern as the text variant below): a native
                `title` on a disabled <button> is not shown by browsers, so the "stop the
                cell" hint would be invisible exactly when it is needed. */}
            <Tooltip title={checked !== 'OFF' ? t_('Stop cell to cancel part') : ''}>
              <span style={{ display: 'flex' }}>
                <button
                  type='button'
                  className='mcx-touch-btn mcx-touch-btn--lg'
                  data-testid='resource-action-cancel'
                  style={{ flex: 1 }}
                  onClick={handleOpenCancelDialog}
                  disabled={checked !== 'OFF' || !props.part.status || props.part.finished || props.part.status === 'RAW' || !isUserAuthorized('CANCEL_PART')}
                >
                  <Cancel sx={{ fontSize: 32 }} />
                  <span className='mcx-touch-btn__label'>{t_('Cancel part')}</span>
                </button>
              </span>
            </Tooltip>
          </>
          )
        : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <>
              {!props.hideNextJob && (
                <Button
                  startIcon={<InputIcon />}
                  onClick={() => setShowNextJobDialog(true)}
                  disabled={props.part.finished || props.part.jobRunning || !isUserAuthorized('NEXT_JOB')}
                >
                  {t_('Next job')}
                </Button>
              )}
              <Button
                startIcon={partPaused ? <PlayCircle /> : <PauseCircle />}
                onClick={partPaused ? onResumeWorkflow : onPauseWorkflow}
                disabled={props.part.finished || !isUserAuthorized('PAUSE_WORKFLOW')}
              >
                {partPaused ? t_('Resume workflow') : t_('Pause workflow')}
              </Button>
              {props.part.status !== 'RAW' && (
                <Tooltip
                  title={checked !== 'OFF' ? t_('Stop cell to cancel part') : ''}
                >
                  <span>
                    <Button
                      startIcon={<Cancel />}
                      onClick={handleOpenCancelDialog}
                      disabled={checked !== 'OFF' || props.part.finished || !isUserAuthorized('CANCEL_PART')}
                    >
                      {t_('Cancel part')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </>
          </div>
          ))}
    </>
  )
}

export default PausePart
